/**
 * SocialPostPreview Component
 *
 * Preview how post will look on each platform.
 * Shows platform-specific rendering with mock UIs.
 */
import { useState } from 'react';
import {
  Facebook,
  Instagram,
  Music2,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  Send,
  MoreHorizontal,
} from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const PLATFORM_CONFIGS = {
  facebook: {
    label: 'Facebook',
    icon: Facebook,
    captionLimit: 63206,
  },
  instagram: {
    label: 'Instagram',
    icon: Instagram,
    captionLimit: 2200,
  },
  tiktok: {
    label: 'TikTok',
    icon: Music2,
    captionLimit: 2200,
  },
};

const FacebookPreview = ({ caption, assets, hashtags, accountName = 'Owaza Martial Arts' }) => {
  const fullCaption = hashtags?.length
    ? `${caption}\n\n${hashtags.map((h) => `#${h}`).join(' ')}`
    : caption;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src="/owaza-logo.png" />
          <AvatarFallback>OW</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-semibold text-sm">{accountName}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Just now</p>
        </div>
        <MoreHorizontal className="h-5 w-5 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
      </div>

      {/* Caption */}
      {caption && (
        <div className="px-3 pb-2">
          <p className="text-sm whitespace-pre-wrap">{fullCaption}</p>
        </div>
      )}

      {/* Media */}
      {assets?.length > 0 && (
        <div className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden">
          {assets[0].asset_type === 'video' ? (
            <video
              src={assets[0].media_url}
              className="w-full h-full object-cover"
              controls
            />
          ) : (
            <img
              src={assets[0].media_url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 dark:border-gray-700">
        <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600">
          <Heart className="h-5 w-5" />
          <span className="text-sm">Like</span>
        </button>
        <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600">
          <MessageCircle className="h-5 w-5" />
          <span className="text-sm">Comment</span>
        </button>
        <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600">
          <Share2 className="h-5 w-5" />
          <span className="text-sm">Share</span>
        </button>
      </div>
    </div>
  );
};

const InstagramPreview = ({ caption, assets, hashtags, accountHandle = 'owazamartialarts' }) => {
  const fullCaption = hashtags?.length
    ? `${caption}\n\n${hashtags.map((h) => `#${h}`).join(' ')}`
    : caption;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <Avatar className="h-8 w-8 ring-2 ring-pink-500 ring-offset-2">
          <AvatarImage src="/owaza-logo.png" />
          <AvatarFallback>OW</AvatarFallback>
        </Avatar>
        <p className="font-semibold text-sm flex-1">{accountHandle}</p>
        <MoreHorizontal className="h-5 w-5 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
      </div>

      {/* Media */}
      {assets?.length > 0 && (
        <div className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden">
          {assets[0].asset_type === 'video' ? (
            <video
              src={assets[0].media_url}
              className="w-full h-full object-cover"
              controls
            />
          ) : (
            <img
              src={assets[0].media_url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-4">
          <Heart className="h-6 w-6" />
          <MessageCircle className="h-6 w-6" />
          <Send className="h-6 w-6" />
        </div>
        <Bookmark className="h-6 w-6" />
      </div>

      {/* Caption */}
      {caption && (
        <div className="px-3 pb-3">
          <p className="text-sm">
            <span className="font-semibold">{accountHandle}</span>{' '}
            <span className="whitespace-pre-wrap">{fullCaption}</span>
          </p>
        </div>
      )}
    </div>
  );
};

const TikTokPreview = ({ caption, assets, hashtags, accountHandle = '@owazamartialarts' }) => {
  const fullCaption = hashtags?.length
    ? `${caption} ${hashtags.map((h) => `#${h}`).join(' ')}`
    : caption;

  return (
    <div className="bg-black rounded-lg shadow-sm max-w-xs mx-auto aspect-[9/16] relative overflow-hidden">
      {/* Video */}
      {assets?.length > 0 && assets[0].asset_type === 'video' && (
        <video
          src={assets[0].media_url}
          className="absolute inset-0 w-full h-full object-cover"
          controls
          loop
        />
      )}

      {/* Overlay content */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />

      {/* Right sidebar */}
      <div className="absolute right-3 bottom-20 flex flex-col items-center gap-4 text-white">
        <div className="flex flex-col items-center">
          <Avatar className="h-10 w-10 border-2 border-white">
            <AvatarImage src="/owaza-logo.png" />
            <AvatarFallback>OW</AvatarFallback>
          </Avatar>
        </div>
        <div className="flex flex-col items-center">
          <Heart className="h-7 w-7" fill="white" />
          <span className="text-xs">123</span>
        </div>
        <div className="flex flex-col items-center">
          <MessageCircle className="h-7 w-7" />
          <span className="text-xs">45</span>
        </div>
        <div className="flex flex-col items-center">
          <Share2 className="h-7 w-7" />
          <span className="text-xs">12</span>
        </div>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-4 left-3 right-14 text-white">
        <p className="font-semibold text-sm mb-1">{accountHandle}</p>
        <p className="text-sm line-clamp-3">{fullCaption}</p>
      </div>
    </div>
  );
};

const SocialPostPreview = ({
  caption,
  assets,
  hashtags,
  targetPlatforms = [],
  platformCaptions = {},
  className,
}) => {
  const [activeTab, setActiveTab] = useState(targetPlatforms[0] || 'instagram');

  const availablePlatforms = targetPlatforms.length > 0
    ? targetPlatforms
    : ['facebook', 'instagram', 'tiktok'];

  const getCaption = (platform) =>
    platformCaptions[platform] || caption || '';

  return (
    <div className={cn('space-y-4', className)}>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${availablePlatforms.length}, 1fr)` }}>
          {availablePlatforms.map((platform) => {
            const config = PLATFORM_CONFIGS[platform];
            const Icon = config?.icon;
            return (
              <TabsTrigger key={platform} value={platform} className="gap-2">
                {Icon && <Icon className="h-4 w-4" />}
                {config?.label || platform}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {availablePlatforms.includes('facebook') && (
          <TabsContent value="facebook" className="mt-4">
            <FacebookPreview
              caption={getCaption('facebook')}
              assets={assets}
              hashtags={hashtags}
            />
          </TabsContent>
        )}

        {availablePlatforms.includes('instagram') && (
          <TabsContent value="instagram" className="mt-4">
            <InstagramPreview
              caption={getCaption('instagram')}
              assets={assets}
              hashtags={hashtags}
            />
          </TabsContent>
        )}

        {availablePlatforms.includes('tiktok') && (
          <TabsContent value="tiktok" className="mt-4">
            <TikTokPreview
              caption={getCaption('tiktok')}
              assets={assets}
              hashtags={hashtags}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Character count warning */}
      {PLATFORM_CONFIGS[activeTab] && (
        <p
          className={cn(
            'text-xs text-center',
            getCaption(activeTab).length > PLATFORM_CONFIGS[activeTab].captionLimit
              ? 'text-red-500'
              : 'text-gray-500 dark:text-gray-400'
          )}
        >
          {getCaption(activeTab).length} / {PLATFORM_CONFIGS[activeTab].captionLimit} characters
        </p>
      )}
    </div>
  );
};

export default SocialPostPreview;
